import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, CircularProgress
} from "@mui/material";
import { ArrowBack, Download } from "@mui/icons-material";
import FolderIcon from "@mui/icons-material/Folder";
import { FolderDocument, Document } from "../../../types/folderTypes";
import Layout from "../../../components/layouts/Layout";
import { toast } from "react-toastify";
import UserFolderService from "../../../service/user/folderService";
import FileViewerModal from "../../../components/folderRepo/FileViewerModal";

const FolderDetail = () => {
    const navigate = useNavigate();
    const { id: folderId } = useParams();
    const [viewerDoc, setViewerDoc] = useState<Document | null>(null);
    const [subfolders, setSubfolders] = useState<FolderDocument[]>([]);
    const [files, setFiles] = useState<Document[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [breadcrumbs, setBreadcrumbs] = useState<{ id?: number, name: string, isRepo?: boolean }[]>([]);

    useEffect(() => {
        if (folderId) {
            fetchFolderDetails();
        }
    }, [folderId]);

    const fetchFolderDetails = async () => {
        try {
            setLoading(true);
            const response = await UserFolderService.getFolderDetail(folderId as string);

            if (response.breadcrumb) {
                setBreadcrumbs([
                    { name: "Space", isRepo: true },
                    ...response.breadcrumb
                ]);
            } else {
                setBreadcrumbs([]);
            }
            setSubfolders(response.folders || []);
            setFiles(response.documents || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleFolderClick = (subfolder: FolderDocument) => {
        navigate(`/folder/${subfolder.id}`);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center py-6">
                    <CircularProgress />
                </div>
            </Layout>
        );
    }

    // const handleDownloadFolder = async (folder: any) => {
    //     try {
    //         await UserFolderService.downloadSingleFolder(folder);
    //         toast.success("Folder downloaded.");
    //     } catch (error: any) {
    //         toast.error(error.message || "Failed to download folder.");
    //     }
    // };

    // const handleDownloadFile = async (documentId: string, documentName: string) => {
    //     try {
    //         await UserFolderService.downloadSingleFile(documentId, documentName);
    //         toast.success(`${documentName} downloaded.`);
    //     } catch (error: any) {
    //         toast.error(error.message || "Failed to download folder.");
    //     }
    // };

    // const handleDownloadZipFolder = async () => {
    //     try {
    //         const folderName = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].name : "Folder";

    //         await UserFolderService.downloadSingleFolder({
    //             id: folderId,
    //             folder_name: folderName
    //         });

    //         toast.success(`${folderName} downloaded successfully!`);
    //     } catch (error) {
    //         toast.error("Failed to download the folder.");
    //     }
    // };


    return (
        <Layout>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBack />
                    </IconButton>
                    {breadcrumbs.length > 0 && breadcrumbs.map((item, index) => (
                        <span key={index} className="inline-flex">
                            <span
                                className={`cursor-pointer ${item.isRepo ? "text-green-600" : "text-[#632DE9]"} hover:underline`}
                                onClick={() => {
                                    if (item.isRepo) {
                                        navigate(`/folderRepo`);
                                    } else {
                                        navigate(`/folder/${item.id}`);
                                    }
                                }}
                            >
                                {item.name}
                            </span>
                            {index < breadcrumbs.length - 1 && <span className="mx-1"> &gt; </span>}
                        </span>
                    ))}
                </div>
            </div>

            {
                (files.length == 0 && subfolders.length == 0) ? (
                    <div className="text-center text-gray-500 py-10">
                        <p>No files or folders found.</p>
                        <p className="mt-2">Create a new folder or upload files to get started.</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-hidden rounded-lg border border-gray-200">
                            <TableContainer className="bg-white">
                                <Table>
                                    <TableHead className="bg-gray-100">
                                        <TableRow>
                                            <TableCell className="font-semibold">#</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Created</TableCell>
                                            <TableCell>Last Modified By</TableCell>
                                            <TableCell>Last Modified At</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>

                                        {/* Display Subfolders First */}
                                        {subfolders.map((subfolder, index) => {
                                            const lastLog = subfolder.activity_logs?.[0];
                                            return (
                                                <TableRow key={subfolder.id}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center">
                                                            <FolderIcon className="text-[#632DE9] mr-2" />
                                                            <span
                                                                className="font-medium text-[#632DE9] cursor-pointer hover:underline"
                                                                onClick={() => handleFolderClick(subfolder)}
                                                            >
                                                                {subfolder.folder_name}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>Folder</TableCell>
                                                    <TableCell>{new Date(subfolder.created_at).toLocaleString()}</TableCell>
                                                    <TableCell>{lastLog?.user_name ?? "—"}</TableCell>
                                                    <TableCell>{lastLog?.updated_at ? new Date(lastLog.updated_at).toLocaleString() : "—"}</TableCell>
                                                </TableRow>
                                            );
                                        })}

                                        {/* Display Files Under Parent Folder */}
                                        {files.map((doc: Document, index) => {
                                            const lastLog = doc.activity_logs?.[0];
                                            return (
                                                <TableRow key={doc.id} className="border-b">
                                                    <TableCell>{subfolders.length + index + 1}</TableCell>
                                                    <TableCell onClick={() => setViewerDoc(doc)}>
                                                        <span className="text-[#632DE9] hover:underline font-medium cursor-pointer">
                                                            {doc.document_name}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>File</TableCell>
                                                    <TableCell>{new Date(doc.created_at).toLocaleString()}</TableCell>
                                                    <TableCell>{lastLog?.user_name ?? "—"}</TableCell>
                                                    <TableCell>{lastLog?.updated_at ? new Date(lastLog.updated_at).toLocaleString() : "—"}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    </>
                )
            }
            <FileViewerModal document={viewerDoc} onClose={() => setViewerDoc(null)} />
        </Layout>
    );
};

export default FolderDetail;
